import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppDispatch, RootState } from '../../store';
import { fetchCover, saveCover } from '../../store/coverSlice';
import { Cover as CoverData } from '../../api';
import { TopNav } from '../../components/TopNav/TopNav';
import {
  ComabooksCover,
  comabooksCovers,
  isComabooksCoverId,
  COMABOOKS_DEFAULT_COVER_ID,
} from '../../components/ComabooksCover';
import '../../styles/margin.css';
import './Cover.css';

/*
 * Обложка Comabooks: 24 пресета старого продукта (4 вёрстки × 6 цветов),
 * превью 1:1 со старым клиентом (ComabooksCover). Каталог Lovestoria,
 * своё фото и plate_preset в Comabooks не показываются — для cb-* печать
 * их не использует (backend services/coverGeneratorComabooks.ts).
 */
export const Cover: React.FC = () => {
  const { id: templateId } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t } = useTranslation('cover');

  const coverData = useSelector<RootState, CoverData | null>(
    (state) => state.cover.value
  );
  const templates = useSelector((state: RootState) => state.templates.templates);
  const recipient = (
    templates.find((tpl) => String(tpl.id) === String(templateId))?.recipient ?? ''
  ).toLowerCase();

  const [authorName, setAuthorName] = useState('');
  const [bookTitle, setBookTitle] = useState('');
  const [partnerName, setPartnerName] = useState('');
  const [hidePartner, setHidePartner] = useState(false);
  const [coverId, setCoverId] = useState<string>(COMABOOKS_DEFAULT_COVER_ID);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/login', { replace: true });
      return;
    }
    if (!templateId) navigate('/forms');
  }, [navigate, templateId]);

  useEffect(() => {
    if (templateId) {
      dispatch(fetchCover(Number(templateId)));
    }
  }, [dispatch, templateId]);

  useEffect(() => {
    if (coverData) {
      setAuthorName(coverData.authorName || '');
      setBookTitle(coverData.bookTitle || '');
      setPartnerName(coverData.receiverName || '');
      setHidePartner(false);
      setCoverId(
        isComabooksCoverId(coverData.coverUrl) ? coverData.coverUrl : COMABOOKS_DEFAULT_COVER_ID
      );
      setHasChanges(false);
    }
  }, [coverData]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleBack = () => {
    navigate('/forms');
  };

  const handleSave = async () => {
    if (!templateId || isSaving) return;
    setIsSaving(true);
    try {
      await dispatch(
        saveCover({
          templateId: Number(templateId),
          bookTitle,
          coverUrl: coverId,
          authorName,
          // «Не отображать на обложке»: в старом Comabooks галочка не
          // сохранялась и имя всё равно уходило в печать; здесь она его убирает.
          receiverName: hidePartner ? '' : partnerName,
        })
      );
      if (hidePartner) {
        setPartnerName('');
        setHidePartner(false);
      }
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving cover:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelect = (id: string) => {
    if (id !== coverId) {
      setCoverId(id);
      setHasChanges(true);
    }
  };

  const receiverLabel = (): string => {
    if (recipient.includes('мам')) return t('receiver_mom') as string;
    if (recipient.includes('сестр')) return t('receiver_sister') as string;
    if (recipient.includes('подруг')) return t('receiver_friend') as string;
    return t('receiver_partner') as string;
  };

  const validateBookTitle = (title: string) => {
    const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u;
    return title.length <= 30 && !emojiRegex.test(title);
  };

  const handleBookTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value;
    if (validateBookTitle(title)) {
      setBookTitle(title);
      setHasChanges(true);
    }
  };

  const handleInputChange = (setter: (val: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setter(e.target.value);
    setHasChanges(true);
  };

  return (
    <div className="mg-page mg-cover">
      <TopNav
        onLogout={handleLogout}
        back={handleBack}
        title={t('rite_now') as string}
      />

      <main className="mg-cover__main mg-split">

        <section className="mg-cover__stage mg-split__preview">
          <ComabooksCover
            className="mg-cover__preview mg-cover__preview--cb"
            coverId={coverId}
            title={bookTitle}
            author={authorName}
            receiver={hidePartner ? '' : partnerName}
          />
        </section>

        <div className="mg-split__ui">

        <section className="mg-cover__gallery">
          <div className="mg-label mg-cover__gallery-head">
            {t('gallery_head_cb')}
          </div>
          <div className="mg-cover__thumbs mg-cover__thumbs--cb">
            {comabooksCovers.map((entry) => (
              <button
                key={entry.id}
                type="button"
                title={entry.name}
                aria-label={entry.name}
                className={`mg-cover__thumb mg-cover__thumb--cb ${entry.id === coverId ? 'mg-cover__thumb--active' : ''}`}
                onClick={() => handleSelect(entry.id)}
              >
                <img src={entry.thumb} alt="" className="mg-cover__thumb-img" loading="lazy" />
              </button>
            ))}
          </div>
        </section>

        <section className="mg-cover__inputs mg-cover__inputs--cb">
          <div className="mg-cover__fields">
            <div className="mg-field">
              <label className="mg-field__label">{t('field_your_name')}</label>
              <input
                type="text"
                className="mg-input"
                placeholder={t('placeholder_author') as string}
                value={authorName}
                onChange={handleInputChange(setAuthorName)}
              />
            </div>

            <div className="mg-field">
              <label className="mg-field__label">{receiverLabel()}</label>
              <input
                type="text"
                className="mg-input"
                placeholder={t('placeholder_recipient') as string}
                value={partnerName}
                onChange={handleInputChange(setPartnerName)}
                disabled={hidePartner}
              />
              <label className="mg-cover__check">
                <input
                  type="checkbox"
                  checked={hidePartner}
                  onChange={() => {
                    setHidePartner(!hidePartner);
                    setHasChanges(true);
                  }}
                />
                {t('hide_on_cover')}
              </label>
            </div>

            <div className="mg-field">
              <label className="mg-field__label">{t('field_book_title')}</label>
              <input
                type="text"
                className="mg-input"
                placeholder={t('placeholder_book_title_cb') as string}
                value={bookTitle}
                onChange={handleBookTitleChange}
                maxLength={30}
              />
              <div className="mg-cover__count mg-meta">
                {bookTitle.length} / 30
              </div>
            </div>
          </div>
        </section>

        <section className="mg-cover__cta">
          <button
            className="mg-btn"
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            type="button"
          >
            {isSaving
              ? t('cta_saving')
              : hasChanges
                ? t('cta_approve')
                : t('cta_approved')}
          </button>
        </section>
        </div>
      </main>
    </div>
  );
};
